import React, { useEffect, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import ImageManagerDialog from './ImageManagerDialog';
import {
  createImageSlot,
  normalizeImageSlot,
  resolveActiveImage,
} from './contentLayout';


const LANGUAGE_NAMES = {
  fr: 'français',
  en: 'anglais',
  pt: 'portugais',
};

const optionValue = (value) =>
  typeof value === 'function' ? value() : value;

const fallbackSlotForNode = (node) =>
  createImageSlot({
    id: node.attrs.imageId,
    proposalId: `proposal_${String(node.attrs.imageId || 'preview').replace(
      /^img_/,
      ''
    )}`,
    url: node.attrs.previewUrl || '',
    captions: {
      fr: node.attrs.previewCaption || '',
      en: node.attrs.previewCaption || '',
      pt: node.attrs.previewCaption || '',
    },
    placement: {
      type: 'inline',
      align: node.attrs.previewAlign || 'center',
      wrap: node.attrs.previewWrap || 'none',
      width: {
        unit: node.attrs.previewWidthUnit || 'percent',
        value: node.attrs.previewWidthValue || 60,
      },
    },
  });

const BookImageNodeView = ({
  node,
  extension,
  selected,
  deleteNode,
}) => {
  const resolvedSlot = extension.options.resolveImageSlot?.(
    node.attrs.imageId
  );
  const [slot, setSlot] = useState(() =>
    normalizeImageSlot(resolvedSlot || fallbackSlotForNode(node))
  );
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    if (!resolvedSlot) return;
    const nextSlot = normalizeImageSlot(resolvedSlot);
    setSlot(nextSlot);
  }, [resolvedSlot]);

  const contentLanguage =
    optionValue(extension.options.contentLanguage) || 'fr';
  const canManageImages = Boolean(
    optionValue(extension.options.canManageImages)
  );
  const canManageImageStructure = Boolean(
    optionValue(extension.options.canManageImageStructure)
  );
  const activeImage = resolveActiveImage(slot, contentLanguage);
  const storedProposal = slot.proposals.find((proposal) => proposal?.url);
  const editorFallbackImage =
    canManageImages && !activeImage && storedProposal
      ? {
          url: storedProposal.url,
          caption: storedProposal.captions?.[contentLanguage] || '',
        }
      : null;
  const displayedImage = activeImage || editorFallbackImage;
  const placement = slot.placement || {};
  const align = placement.align || node.attrs.previewAlign || 'center';
  const wrap = placement.wrap || node.attrs.previewWrap || 'none';
  const width = placement.width || {
    unit: node.attrs.previewWidthUnit || 'percent',
    value: node.attrs.previewWidthValue || 60,
  };
  const widthValue = Number(width.value);
  const style = Number.isFinite(widthValue)
    ? {
        width: `${widthValue}${width.unit === 'px' ? 'px' : '%'}`,
        maxWidth: '100%',
      }
    : undefined;

  const commitSlot = (nextSlot) => {
    setSlot(nextSlot);
    extension.options.onChangeImageSlot?.(nextSlot);
  };

  return (
    <NodeViewWrapper
      as="figure"
      data-book-image-id={node.attrs.imageId}
      className={[
        'book-inline-image',
        `book-inline-image--${align}`,
        wrap === 'around' ? 'book-inline-image--wrap' : '',
        selected ? 'book-image-anchor--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      contentEditable={false}
      draggable={canManageImageStructure}
    >
      {displayedImage && (
        <>
          <img
            src={displayedImage.url}
            alt={displayedImage.caption || ''}
            draggable={false}
          />
          {displayedImage.caption && (
            <figcaption>{displayedImage.caption}</figcaption>
          )}
        </>
      )}
      {!activeImage && (
        <span className="book-image-anchor-label">
          Image masquée en {LANGUAGE_NAMES[contentLanguage] || contentLanguage}
        </span>
      )}

      {canManageImages && (
        <button
          type="button"
          className="book-inline-image-edit-button"
          aria-label="Modifier l’image"
          onClick={(event) => {
            event.stopPropagation();
            setShowControls(true);
          }}
        >
          Modifier l’image
        </button>
      )}

      {canManageImages && (
        <ImageManagerDialog
          open={showControls}
          slot={slot}
          canEdit
          canManageStructure={canManageImageStructure}
          onUploadImage={extension.options.onUploadImage}
          onClose={() => setShowControls(false)}
          onSave={(nextSlot) => {
            commitSlot(nextSlot);
            setShowControls(false);
          }}
          onDeleteSlot={
            canManageImageStructure
              ? () => {
                  extension.options.onDeleteImageSlot?.(slot.id);
                  deleteNode();
                  setShowControls(false);
                }
              : undefined
          }
          onReturnToGallery={
            canManageImageStructure
              ? () => {
                  extension.options.onReturnToGallery?.(slot.id);
                  deleteNode();
                  setShowControls(false);
                }
              : undefined
          }
        />
      )}
    </NodeViewWrapper>
  );
};

const BookImageNode = Node.create({
  name: 'bookImage',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      resolveImageSlot: () => undefined,
      onChangeImageSlot: () => undefined,
      onReturnToGallery: () => undefined,
      onDeleteImageSlot: () => undefined,
      onUploadImage: null,
      contentLanguage: 'fr',
      canManageImages: false,
      canManageImageStructure: false,
    };
  },

  addAttributes() {
    return {
      imageId: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute('data-book-image-id'),
        renderHTML: () => ({}),
      },
      previewUrl: {
        default: null,
        renderHTML: () => ({}),
      },
      previewCaption: {
        default: null,
        renderHTML: () => ({}),
      },
      previewAlign: {
        default: null,
        renderHTML: () => ({}),
      },
      previewWrap: {
        default: null,
        renderHTML: () => ({}),
      },
      previewWidthUnit: {
        default: null,
        renderHTML: () => ({}),
      },
      previewWidthValue: {
        default: null,
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'figure[data-book-image-id]' }];
  },

  renderHTML({ node }) {
    return [
      'figure',
      mergeAttributes({
        'data-book-image-id': node.attrs.imageId,
      }),
    ];
  },

  addCommands() {
    return {
      removeBookImage:
        ({ imageId }) =>
        ({ state, tr, dispatch }) => {
          if (
            typeof imageId !== 'string' ||
            !/^img_[A-Za-z0-9-]{1,64}$/.test(imageId)
          ) {
            return false;
          }
          const imageType = state.schema.nodes.bookImage;
          if (!imageType) return false;
          const matches = [];
          state.doc.descendants((node, position) => {
            if (
              node.type === imageType &&
              node.attrs.imageId === imageId
            ) {
              matches.push({ position, size: node.nodeSize });
            }
          });
          if (!matches.length) return false;
          matches
            .sort((left, right) => right.position - left.position)
            .forEach(({ position, size }) => {
              tr.delete(position, position + size);
            });
          dispatch?.(tr.scrollIntoView());
          return true;
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(BookImageNodeView);
  },
});

export const insertBookImagesAtPosition = (
  editor,
  position,
  attributesList
) => {
  if (
    !editor ||
    !Number.isInteger(position) ||
    !Array.isArray(attributesList) ||
    attributesList.length === 0
  ) {
    return false;
  }
  const maxPosition = editor.state.doc.content.size;
  const safePosition = Math.max(0, Math.min(position, maxPosition));
  return editor
    .chain()
    .focus()
    .insertContentAt(
      safePosition,
      attributesList.map((attributes) => ({
        type: 'bookImage',
        attrs: attributes,
      })),
      { updateSelection: true }
    )
    .run();
};

export const insertBookImageAtPosition = (
  editor,
  position,
  attributes
) => insertBookImagesAtPosition(editor, position, [attributes]);

export default BookImageNode;
